'use strict';
import React, { useState, useMemo, useEffect } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  Users, Plus, Search, Edit2, Trash2,
  X, UserCheck, Briefcase, Check,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { academyAPI } from '../../../services/api';
import { apiCall } from '../../../services/apiUtil';
import { getCurrentUser } from '../../../profile/services';
import InviteModal from '../../../components/common/InviteModal';
import { resolveMediaUrl } from '../../../utils/helpers';

const STAFF_ROLES = [
  // The only privileged role: full access to everything, same as the owner
  // (isAcademyAdmin on the server). Offered ONLY to a viewer who is already
  // owner/admin — the API refuses to mint an admin for anyone else, so showing
  // it to a manage_staff holder would just hand them a 403.
  'admin',
  // School leadership
  'principal',
  'vice principal',
  'director of athletics',
  'pe department head',
  // Academy leadership
  'owner',
  'ceo',
  'academy director',
  'general manager',
  'operations manager',
  'technical director',
  // Coordination & administration
  'sports coordinator',
  'program manager',
  'program coordinator',
  'events coordinator',
  'membership coordinator',
  'sports administrator',
  'academy administrator',
  'transportation coordinator',
  'marketing manager',
  'front desk',
  'customer service',
  // Coaching
  'head coach',
  'senior coach',
  'coach',
  'assistant coach',
  'junior coach',
  'physical education teacher',
  // Training & fitness
  'strength coach',
  'fitness trainer',
  'athletic trainer',
  // Medical & wellness
  'physiotherapist',
  'sports therapist',
  'sports psychologist',
  'nutritionist',
  'school nurse',
  // Performance
  'performance analyst',
  'video analyst',
  'scout',
  // Operations
  'team manager',
  'equipment manager',
  'facility manager',
  'groundsman',
  // Officiating
  'referee',
  'scorekeeper',
  // Other
  'volunteer',
  'support',
];
const CUSTOM_ROLE = '__custom__';

const ROLE_COLORS = {
  owner:           'bg-purple-100 text-purple-700',
  admin:           'bg-red-100 text-red-700',
  coach:           'bg-yellow-100 text-yellow-700',
  // ⚠️ CATEGORICAL, NOT BRAND. These name a role — admin red, coach yellow,
  // trainer blue, physio green — so the blue here means "trainer" and must not
  // follow the academy's theme. It is also module scope, with no theme to read.
  trainer:         'bg-blue-100 text-blue-700',
  physiotherapist: 'bg-green-100 text-green-700',
  support:         'bg-gray-100 text-gray-700',
};

// Mirrors AcademyCapability in prisma/schema/academies.prisma — a non-admin
// staff member (role != 'admin') has none of these by default; each is an
// individual grant an owner/admin can toggle per person (default-deny).
const CAPABILITIES = [
  { id: 'manage_content',     label: 'Content' },
  { id: 'manage_staff',       label: 'Staff' },
  { id: 'manage_members',     label: 'Members' },
  { id: 'manage_programs',    label: 'Programs' },
  { id: 'manage_tournaments', label: 'Tournaments' },
  { id: 'manage_courts',      label: 'Courts' },
  // Split out of Programs. Front Desk and Reports both used to ride on
  // manage_programs, so the coach running Wednesday's session also held the
  // till and could read the academy's revenue.
  { id: 'manage_open_play',   label: 'Open Play' },
  { id: 'manage_front_desk',  label: 'Front Desk' },
  { id: 'view_reports',       label: 'Reports' },
  // Deciding what the academy offers at all, which is not the same job as
  // editing its content.
  { id: 'manage_features',    label: 'Features' },
];

/**
 * Removing admin access needs one more decision than granting it: the staff row
 * has to land on some other role, and silently choosing one would rewrite
 * someone's job title behind the user's back. So we ask, in one small dialog,
 * instead of bouncing the user into the general Edit Staff modal — which looked
 * enough like nothing had happened that it read as a broken button.
 */
const RemoveAdminDialog = ({ member, assignableRoles, busy, onConfirm, onCancel, theme }) => {
  const [newRole, setNewRole] = useState('coach');
  useEffect(() => { if (member) setNewRole('coach'); }, [member]);
  if (!member) return null;

  const choices = (assignableRoles || []).filter(r => r !== 'admin');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className={`${theme.card} border rounded-2xl w-full max-w-sm shadow-2xl`}>
        <div className={`flex items-center justify-between p-5 border-b ${theme.border}`}>
          <h3 className={`text-xl font-bold ${theme.text}`}>Remove Admin Access</h3>
          <button onClick={onCancel} className={`p-2 rounded-lg ${theme.cardHover}`}>
            <X size={20} className={theme.textSecondary} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className={`text-sm ${theme.textSecondary}`}>
            <span className={`font-semibold ${theme.text}`}>{member.name}</span> will lose full
            access to this academy. Any access rights they held before are restored.
          </p>
          <div>
            <label className={`block text-sm font-medium ${theme.text} mb-1`}>New role</label>
            <select
              value={newRole}
              onChange={e => setNewRole(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg ${theme.input} focus:ring-2 focus:ring-current`}
            >
              {choices.map(r => (
                <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
              ))}
            </select>
          </div>
          <div className={`flex gap-3 pt-2 border-t ${theme.border}`}>
            <button onClick={onCancel} className={`flex-1 px-4 py-2 border ${theme.border} ${theme.text} ${theme.cardHover} rounded-lg`}>
              Cancel
            </button>
            <button
              disabled={busy}
              onClick={() => onConfirm(member, newRole)}
              className={`flex-1 px-4 py-2 ${theme.buttonDanger} rounded-lg disabled:opacity-50`}
            >
              {busy ? 'Removing…' : 'Remove Admin'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Edit Role Modal ────────────────────────────────────────────────────────────

const NO_REPORTS_TO = '__none__';

const EditRoleModal = ({ isOpen, staff, staffOptions, assignableRoles = STAFF_ROLES, onClose, onSave, theme }) => {
  const [role, setRole]           = useState('coach');
  const [customRole, setCustomRole] = useState('');
  const [reportsTo, setReportsTo] = useState(NO_REPORTS_TO);
  const [saving, setSaving]       = useState(false);

  useEffect(() => {
    if (!isOpen || !staff) return;
    const isPredefined = STAFF_ROLES.includes(staff.role);
    setRole(isPredefined ? staff.role : CUSTOM_ROLE);
    setCustomRole(isPredefined ? '' : (staff.role || ''));
    setReportsTo(staff.reportsToStaffId != null ? String(staff.reportsToStaffId) : NO_REPORTS_TO);
  }, [isOpen, staff]);

  if (!isOpen || !staff) return null;

  const reportsToChoices = (staffOptions || []).filter(s => s.id !== staff.id);

  const handleSubmit = async () => {
    const effectiveRole = role === CUSTOM_ROLE ? customRole.trim() : role;
    if (!effectiveRole) { toast.error('Please enter a role name'); return; }
    setSaving(true);
    try {
      await onSave(staff.id, {
        role: effectiveRole,
        reportsToStaffId: reportsTo === NO_REPORTS_TO ? null : Number(reportsTo),
      });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className={`${theme.card} border rounded-2xl w-full max-w-sm shadow-2xl`}>
        <div className={`flex items-center justify-between p-5 border-b ${theme.border}`}>
          <h3 className={`text-xl font-bold ${theme.text}`}>Edit Staff</h3>
          <button onClick={onClose} className={`p-2 rounded-lg ${theme.cardHover}`}>
            <X size={20} className={theme.textSecondary} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className={`text-sm ${theme.textSecondary}`}>
            Updating <span className={`font-semibold ${theme.text}`}>{staff.name}</span>
          </p>
          <div>
            <label className={`block text-sm font-medium ${theme.text} mb-1`}>Role</label>
            <select
              value={role}
              onChange={e => setRole(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg ${theme.input} focus:ring-2 focus:ring-current`}
            >
              {assignableRoles.map(r => (
                <option key={r} value={r}>
                  {r === 'admin' ? 'Admin — full access' : r.charAt(0).toUpperCase() + r.slice(1)}
                </option>
              ))}
              <option value={CUSTOM_ROLE}>Custom…</option>
            </select>
          </div>
          {role === CUSTOM_ROLE && (
            <input
              type="text"
              value={customRole}
              onChange={e => setCustomRole(e.target.value)}
              placeholder="e.g. Video Analyst, Goalkeeping Coach"
              className={`w-full px-3 py-2 border rounded-lg ${theme.input} focus:ring-2 focus:ring-current`}
              autoFocus
            />
          )}
          <div>
            <label className={`block text-sm font-medium ${theme.text} mb-1`}>Reports to</label>
            <select
              value={reportsTo}
              onChange={e => setReportsTo(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg ${theme.input} focus:ring-2 focus:ring-current`}
            >
              <option value={NO_REPORTS_TO}>No one (none)</option>
              {reportsToChoices.map(s => (
                <option key={s.id} value={s.id}>{s.name} — {s.role}</option>
              ))}
            </select>
          </div>
          <div className={`flex gap-3 pt-2 border-t ${theme.border}`}>
            <button onClick={onClose} className={`flex-1 px-4 py-2 border ${theme.border} ${theme.text} ${theme.cardHover} rounded-lg`}>
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className={`flex-1 px-4 py-2 ${theme.buttonPrimary} rounded-lg font-medium disabled:opacity-60`}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Delete Confirmation Dialog ─────────────────────────────────────────────────

const DeleteConfirmDialog = ({ item, label, onConfirm, onCancel, theme }) => {
  if (!item) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className={`${theme.card} border rounded-2xl w-full max-w-sm shadow-2xl p-6`}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
            <Trash2 size={18} className="text-pink-600" />
          </div>
          <h3 className={`text-lg font-bold ${theme.text}`}>{label}</h3>
        </div>
        <p className={`${theme.textSecondary} text-sm mb-6`}>This action cannot be undone.</p>
        <div className="flex gap-3">
          <button onClick={onCancel} className={`flex-1 px-4 py-2 border ${theme.border} ${theme.text} ${theme.cardHover} rounded-lg`}>Cancel</button>
          <button onClick={onConfirm} className={`flex-1 px-4 py-2 ${theme.buttonDanger} rounded-lg font-medium`}>Remove</button>
        </div>
      </div>
    </div>
  );
};

// ── Opening Card ───────────────────────────────────────────────────────────────

const STATUS_COLORS = {
  open:   'bg-green-100 text-green-700',
  filled: 'bg-blue-100 text-blue-700',
  closed: 'bg-gray-100 text-gray-700',
};

const OpeningCard = ({ opening, isAdmin, onEdit, onDelete, theme }) => (
  <div className={`${theme.card} border rounded-xl p-5 ${theme.cardHover} transition-colors`}>
    <div className="flex items-start justify-between gap-3 mb-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_COLORS[opening.status] || 'bg-gray-100 text-gray-700'}`}>
            {opening.status.charAt(0).toUpperCase() + opening.status.slice(1)}
          </span>
          {opening.role && (
            <span className={`text-xs px-2 py-0.5 rounded-full bg-blue-50 ${theme.link} font-medium`}>{opening.role}</span>
          )}
        </div>
        <h4 className={`text-base font-bold ${theme.text}`}>{opening.title}</h4>
      </div>
      <Briefcase size={20} className={`${theme.link} flex-shrink-0 mt-0.5`} />
    </div>

    {opening.description && (
      <p className={`text-sm ${theme.textSecondary} line-clamp-3 mb-3`}>{opening.description}</p>
    )}

    {(opening.reportsTo || opening.hiringManager) && (
      <div className={`text-xs ${theme.textMuted} mb-4 space-y-1`}>
        {opening.reportsTo && <div>Reports to: <span className="font-medium">{opening.reportsTo.name}</span></div>}
        {opening.hiringManager && <div>Hiring manager: <span className="font-medium">{opening.hiringManager.name}</span></div>}
      </div>
    )}

    {isAdmin ? (
      <div className="flex gap-2">
        <button onClick={() => onEdit(opening)}
          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 border ${theme.border} ${theme.text} ${theme.cardHover} rounded-lg text-sm transition-colors`}>
          <Edit2 size={14} /> Edit
        </button>
        <button onClick={() => onDelete(opening)}
          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 ${theme.buttonDanger} rounded-lg text-sm`}>
          <Trash2 size={14} /> Remove
        </button>
      </div>
    ) : (
      <button className={`w-full py-2 rounded-lg text-sm font-semibold ${theme.buttonPrimary}`}>Apply Now</button>
    )}
  </div>
);

// ── Opening Form Modal ─────────────────────────────────────────────────────────

const OPENING_STATUSES = ['open', 'filled', 'closed'];

const EMPTY_OPENING_FORM = {
  title: '', role: '', reportsToStaffId: NO_REPORTS_TO, hiringManagerStaffId: NO_REPORTS_TO,
  description: '', status: 'open',
};

const toOpeningForm = (opening) => opening ? {
  title:                opening.title || '',
  role:                 opening.role || '',
  reportsToStaffId:     opening.reportsToStaffId != null ? String(opening.reportsToStaffId) : NO_REPORTS_TO,
  hiringManagerStaffId: opening.hiringManagerStaffId != null ? String(opening.hiringManagerStaffId) : NO_REPORTS_TO,
  description:          opening.description || '',
  status:                opening.status || 'open',
} : EMPTY_OPENING_FORM;

const OpeningFormModal = ({ isOpen, onClose, onSave, initialData, staffOptions, theme }) => {
  const [form, setForm]     = useState(toOpeningForm(initialData));
  const [saving, setSaving] = useState(false);
  const isEditing = !!initialData;

  useEffect(() => { if (isOpen) setForm(toOpeningForm(initialData)); }, [isOpen, initialData]);

  if (!isOpen) return null;

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Role title is required'); return; }
    setSaving(true);
    try {
      await onSave({
        title:                form.title.trim(),
        role:                 form.role.trim() || null,
        reportsToStaffId:     form.reportsToStaffId === NO_REPORTS_TO ? null : Number(form.reportsToStaffId),
        hiringManagerStaffId: form.hiringManagerStaffId === NO_REPORTS_TO ? null : Number(form.hiringManagerStaffId),
        description:          form.description.trim() || null,
        status:                form.status,
      });
    } finally { setSaving(false); }
  };

  const inputCls = `w-full px-3 py-2 border rounded-lg ${theme.input} focus:ring-2 focus:ring-current focus:border-transparent`;
  const labelCls = `block text-sm font-medium ${theme.text} mb-1`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className={`${theme.card} border rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl`}>
        <div className={`flex items-center justify-between p-5 border-b ${theme.border}`}>
          <div className="flex items-center gap-2">
            <Briefcase size={20} className={`${theme.link}`} />
            <h3 className={`text-xl font-bold ${theme.text}`}>{isEditing ? 'Edit Opening' : 'Post Opening'}</h3>
          </div>
          <button onClick={onClose} className={`p-2 rounded-lg ${theme.cardHover}`}><X size={20} className={theme.textSecondary} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className={labelCls}>Role Title <span className="text-red-500">*</span></label>
            <input type="text" value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Head Tennis Coach" className={inputCls} required />
          </div>
          <div>
            <label className={labelCls}>Role</label>
            <input type="text" value={form.role} onChange={e => set('role', e.target.value)} placeholder="e.g. coach, trainer, admin" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Reports to</label>
            <select value={form.reportsToStaffId} onChange={e => set('reportsToStaffId', e.target.value)} className={inputCls}>
              <option value={NO_REPORTS_TO}>No one (none)</option>
              {(staffOptions || []).map(s => (
                <option key={s.id} value={s.id}>{s.name} — {s.role}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Hiring manager</label>
            <select value={form.hiringManagerStaffId} onChange={e => set('hiringManagerStaffId', e.target.value)} className={inputCls}>
              <option value={NO_REPORTS_TO}>Unassigned</option>
              {(staffOptions || []).map(s => (
                <option key={s.id} value={s.id}>{s.name} — {s.role}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Description</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)}
              placeholder="Describe the role and responsibilities..." rows={4} className={`${inputCls} resize-none`} />
          </div>
          {isEditing && (
            <div>
              <label className={labelCls}>Status</label>
              <select value={form.status} onChange={e => set('status', e.target.value)} className={inputCls}>
                {OPENING_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
            </div>
          )}
          <div className={`flex gap-3 pt-2 border-t ${theme.border}`}>
            <button type="button" onClick={onClose} className={`flex-1 px-4 py-2 border ${theme.border} ${theme.text} ${theme.cardHover} rounded-lg`}>Cancel</button>
            <button type="submit" disabled={saving} className={`flex-1 px-4 py-2 ${theme.buttonPrimary} rounded-lg font-medium disabled:opacity-60`}>
              {saving ? 'Saving…' : isEditing ? 'Save Changes' : 'Post Opening'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── StaffTab ───────────────────────────────────────────────────────────────────

const StaffTab = ({ academyId, userRole, isAdmin: isAdminProp, myCapabilities = [], onAcademyUpdate }) => {
  const { theme } = useTheme();

  const [staff, setStaff]                   = useState([]);
  const [loadingStaff, setLoadingStaff]     = useState(true);
  const [searchQuery, setSearchQuery]       = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingStaff, setEditingStaff]     = useState(null);
  const [deletingStaff, setDeletingStaff]   = useState(null);
  const [demotingAdmin, setDemotingAdmin]   = useState(null);

  const [openings, setOpenings]                   = useState([]);
  const [loadingOpenings, setLoadingOpenings]     = useState(true);
  const [showOpeningModal, setShowOpeningModal]   = useState(false);
  const [editingOpening, setEditingOpening]       = useState(null);
  const [deletingOpening, setDeletingOpening]     = useState(null);

  const isAdmin = isAdminProp ?? (userRole === 'admin' || userRole === 'owner');
  // The server lets anyone holding manage_staff administer staff and grants —
  // only minting a NEW admin stays owner/admin-only (requireAdminAccess in
  // addStaff/updateStaffRole). This screen used to hide behind isAdmin alone,
  // so a manage_staff holder saw a read-only page the API would have obeyed —
  // and with no admin around, nobody could grant anything at all.
  const canManageStaff = isAdmin || myCapabilities.includes('manage_staff');
  // Minting an admin is owner/admin-only server-side (requireAdminAccess in
  // addStaff and updateStaffRole), so don't offer the option to anyone else.
  const assignableRoles = isAdmin ? STAFF_ROLES : STAFF_ROLES.filter(r => r !== 'admin');
  // The owner's own row is theirs alone — an admin must not be able to edit or
  // remove it (assertOwnerRowProtected on the server).
  const isOwnerViewer = userRole === 'owner';
  // Per-capability toggle in flight (staffId-capability key) — disables that
  // one chip and lets the rest of the matrix keep working during the request.
  const [pendingCapability, setPendingCapability] = useState(null);

  // Load staff list
  useEffect(() => {
    if (!academyId) return;
    setLoadingStaff(true);
    academyAPI.staff.list(academyId)
      .then(data => setStaff(Array.isArray(data) ? data : []))
      .catch(() => toast.error('Failed to load staff'))
      .finally(() => setLoadingStaff(false));
  }, [academyId]);

  // Load open positions
  useEffect(() => {
    if (!academyId) return;
    setLoadingOpenings(true);
    academyAPI.openPositions.list(academyId)
      .then(data => setOpenings(Array.isArray(data) ? data : []))
      .catch(() => toast.error('Failed to load open positions'))
      .finally(() => setLoadingOpenings(false));
  }, [academyId]);

  const filteredStaff = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return staff;
    return staff.filter(s => s.name.toLowerCase().includes(q));
  }, [staff, searchQuery]);

  // ── InviteModal callbacks ──────────────────────────────────────────────────

  // Immediate per-toggle save (not a batch "Save" button) — for an access-control
  // feature, an unsaved pending state is a real footgun (owner thinks they've
  // revoked access, it's still live). Optimistic UI, reverts on failure.
  const handleToggleCapability = async (member, capabilityId, currentlyGranted) => {
    const key = `${member.id}-${capabilityId}`;
    setPendingCapability(key);
    setStaff(prev => prev.map(s => s.id !== member.id ? s : {
      ...s,
      capabilities: currentlyGranted
        ? s.capabilities.filter(c => c !== capabilityId)
        : [...s.capabilities, capabilityId],
    }));
    try {
      const action = currentlyGranted ? academyAPI.staff.revokeCapability : academyAPI.staff.grantCapability;
      const { capabilities } = await action(academyId, member.id, capabilityId);
      setStaff(prev => prev.map(s => s.id === member.id ? { ...s, capabilities } : s));
    } catch (err) {
      // Revert the optimistic flip on failure.
      setStaff(prev => prev.map(s => s.id !== member.id ? s : {
        ...s,
        capabilities: currentlyGranted
          ? [...s.capabilities, capabilityId]
          : s.capabilities.filter(c => c !== capabilityId),
      }));
      toast.error(err?.message || 'Failed to update access');
    } finally {
      setPendingCapability(null);
    }
  };

  const handleAddExistingMember = async (user, role) => {
    const newStaff = await academyAPI.staff.add(academyId, {
      targetUserId: user.id,
      role: role || 'coach',
    });
    setStaff(prev => [...prev, newStaff]);
    toast.success(`${newStaff.name} added as ${newStaff.role}`);
    onAcademyUpdate?.();
  };

  const handleInviteByEmail = async ({ firstName, lastName, email }) => {
    const currentUser = getCurrentUser();
    const inviterName = currentUser
      ? `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || 'Academy admin'
      : 'Academy admin';

    await apiCall('/invitations/group', {
      method: 'POST',
      body: JSON.stringify({
        email,
        firstName,
        lastName,
        groupId: null,
        groupName: null,
        inviterName,
        useBy: `academy_staff:${academyId}`,
      }),
    });
    toast.success(`Invitation sent to ${firstName} ${lastName}`);
  };

  const handleInviteByPhone = async ({ firstName, lastName, phone, countryCode }) => {
    await apiCall('/invitations', {
      method: 'POST',
      body: JSON.stringify({
        phoneNumber: countryCode + phone,
        firstName,
        lastName,
        useBy: `academy_staff:${academyId}`,
        status: 'pending',
      }),
    });
    toast.success(`Invitation sent to ${firstName} ${lastName}`);
  };

  const checkIfAlreadyExists = (userId) =>
    staff.some(s => String(s.userId) === userId || String(s.id) === userId);

  // ── Staff handlers ─────────────────────────────────────────────────────────


  /**
   * The Admin chip is not a capability — it's the staff ROLE, shown alongside
   * the capability chips because "what can this person do" is one question to
   * the person asking it. An admin holds everything implicitly (isAcademyAdmin
   * on the server), so there are no grant rows to toggle.
   *
   * Turning it OFF can't be silent: the row needs *some* role, and inventing
   * one would quietly rewrite someone's job title. So it opens the Edit Staff
   * modal and lets you choose what they become.
   */
  const handleToggleAdmin = async (member) => {
    if (member.isOwner) return;                 // ownership isn't a toggle
    // Removing admin needs a replacement role, so it gets its own small
    // dialog. It used to reuse the general Edit Staff modal, which looked like
    // the click had simply done nothing.
    if (member.fullAccess ?? member.role === 'admin') { setDemotingAdmin(member); return; }

    setPendingCapability(`${member.id}-admin`);
    try {
      const updated = await academyAPI.staff.updateRole(academyId, member.id, {
        role:             'admin',
        reportsToStaffId: member.reportsToStaffId ?? null,
      });
      setStaff(prev => prev.map(s => s.id === member.id ? updated : s));
      toast.success(`${member.name} now has full admin access`);
      onAcademyUpdate?.();
    } catch (err) {
      toast.error(err?.message || 'Failed to grant admin access');
    } finally {
      setPendingCapability(null);
    }
  };

  const handleRemoveAdmin = async (member, newRole) => {
    setPendingCapability(`${member.id}-admin`);
    try {
      const updated = await academyAPI.staff.updateRole(academyId, member.id, {
        role:             newRole,
        reportsToStaffId: member.reportsToStaffId ?? null,
      });
      setStaff(prev => prev.map(s => s.id === member.id ? updated : s));
      toast.success(`Admin access removed from ${member.name}`);
      setDemotingAdmin(null);
      onAcademyUpdate?.();
    } catch (err) {
      toast.error(err?.message || 'Failed to remove admin access');
    } finally {
      setPendingCapability(null);
    }
  };

  const handleEditRole = async (staffId, { role, reportsToStaffId }) => {
    try {
      const updated = await academyAPI.staff.updateRole(academyId, staffId, { role, reportsToStaffId });
      setStaff(prev => prev.map(s => s.id === staffId ? updated : s));
      toast.success('Staff member updated');
      setEditingStaff(null);
      onAcademyUpdate?.(); // role change can move someone in/out of the Coaches count
    } catch (err) {
      toast.error(err?.message || 'Failed to update staff member');
    }
  };

  const handleRemoveStaff = async () => {
    if (!deletingStaff) return;
    try {
      await academyAPI.staff.remove(academyId, deletingStaff.id);
      setStaff(prev => prev.filter(s => s.id !== deletingStaff.id));
      toast.success(`${deletingStaff.name} removed from staff`);
      onAcademyUpdate?.();
    } catch (err) {
      toast.error(err?.message || 'Failed to remove staff');
    } finally {
      setDeletingStaff(null);
    }
  };

  // ── Opening handlers ───────────────────────────────────────────────────────

  const handleSaveOpening = async (formData) => {
    try {
      if (editingOpening) {
        const updated = await academyAPI.openPositions.update(academyId, editingOpening.id, formData);
        setOpenings(prev => prev.map(o => o.id === editingOpening.id ? updated : o));
        toast.success('Opening updated');
      } else {
        const created = await academyAPI.openPositions.create(academyId, formData);
        setOpenings(prev => [created, ...prev]);
        toast.success('Opening posted');
      }
      setShowOpeningModal(false);
      setEditingOpening(null);
    } catch (err) {
      toast.error(err?.message || 'Failed to save opening');
    }
  };

  const handleDeleteOpening = async () => {
    if (!deletingOpening) return;
    try {
      await academyAPI.openPositions.remove(academyId, deletingOpening.id);
      setOpenings(prev => prev.filter(o => o.id !== deletingOpening.id));
      toast.success('Opening removed');
    } catch (err) {
      toast.error(err?.message || 'Failed to remove opening');
    } finally {
      setDeletingOpening(null);
    }
  };

  return (
    <div>
      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h3 className={`text-2xl font-bold ${theme.text}`}>Staff</h3>
          <p className={theme.textSecondary}>
            {loadingStaff ? 'Loading…' : `${staff.length} staff member${staff.length !== 1 ? 's' : ''}`}
            {openings.length > 0 && ` · ${openings.length} open role${openings.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        {canManageStaff && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setEditingOpening(null); setShowOpeningModal(true); }}
              className={`flex items-center gap-1.5 px-4 py-2 border ${theme.border} ${theme.text} ${theme.cardHover} rounded-lg font-medium text-sm transition-colors`}
            >
              <Briefcase size={16} />
              <span>Post Opening</span>
            </button>
            <button
              onClick={() => setShowInviteModal(true)}
              className={`flex items-center gap-1.5 px-4 py-2 ${theme.buttonPrimary} rounded-lg font-medium text-sm`}
            >
              <Plus size={16} />
              <span>Add Staff</span>
            </button>
          </div>
        )}
      </div>

      {/* ── Search ── */}
      <div className="relative mb-6 max-w-sm">
        <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${theme.textMuted}`} />
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search staff by name..."
          className={`w-full pl-9 pr-4 py-2 border rounded-lg ${theme.input} focus:ring-2 focus:ring-current focus:border-transparent`}
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')} className={`absolute right-3 top-1/2 -translate-y-1/2 ${theme.textMuted} hover:text-red-500`}>
            <X size={14} />
          </button>
        )}
      </div>

      {/* ── Staff Cards ── */}
      {loadingStaff ? (
        <div className={`${theme.card} border text-center p-12 rounded-xl`}>
          <div className={`text-sm ${theme.textSecondary}`}>Loading staff…</div>
        </div>
      ) : filteredStaff.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredStaff.map(member => (
            <div key={member.id} className={`${theme.card} border rounded-xl p-6 text-center ${theme.cardHover} transition-colors`}>
              <div className="relative inline-block mb-4">
                {member.profileImage ? (
                  <img
                    src={resolveMediaUrl(member.profileImage)}
                    alt={member.name}
                    className="w-24 h-24 rounded-full mx-auto object-cover"
                    onError={e => {
                      e.target.onerror = null;
                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&background=3b82f6&color=fff&size=96`;
                    }}
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full mx-auto bg-blue-100 flex items-center justify-center">
                    <UserCheck size={36} className={`${theme.link}`} />
                  </div>
                )}
              </div>

              <h5 className={`text-lg font-semibold ${theme.text} mb-1`}>{member.name}</h5>
              <span className={`inline-block px-3 py-1 rounded-lg text-xs font-semibold mb-3 ${ROLE_COLORS[member.role] || 'bg-gray-100 text-gray-700'}`}>
                {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
              </span>

              {member.email && (
                <p className={`${theme.textSecondary} text-sm mb-1 truncate`}>{member.email}</p>
              )}

              {member.reportsTo && (
                <p className={`${theme.textMuted} text-xs mb-4`}>Reports to: {member.reportsTo.name}</p>
              )}

              {/* ACCESS RIGHTS.
                  The Admin chip sits first and is a different animal from the
                  rest: the others are capability grant rows, Admin is the staff
                  ROLE. An admin (and the owner) holds every capability
                  implicitly via isAcademyAdmin on the server, so when Admin is
                  on there are no individual grants left to show — the ten chips
                  give way to a single line.

                  Toggling any of this is owner/admin only, and the server agrees:
                  grantCapability/revokeCapability and role changes to or from
                  'admin' all call requireAdminAccess. manage_staff must NOT
                  reach here — its holder could aim a grant at their own row and
                  collect all ten, which is admin by the back door. */}
              <div className="mb-4 text-left">
                <p className={`text-xs font-medium ${theme.textMuted} mb-1.5`}>Access rights</p>
                <div className="flex flex-wrap gap-1.5">
                  {(() => {
                    const full      = member.fullAccess ?? member.role === 'admin';
                    const isPending = pendingCapability === `${member.id}-admin`;
                    // Ownership isn't a toggle, and non-admin viewers get a
                    // read-only badge rather than a dead button.
                    const locked    = member.isOwner || !isAdmin;
                    if (!isAdmin && !full) return null;   // nothing to say on an ordinary row
                    return (
                      <button
                        disabled={locked || isPending}
                        onClick={() => handleToggleAdmin(member)}
                        title={
                          member.isOwner ? 'The academy owner always has full access'
                            : !isAdmin   ? 'Full admin access'
                            : full       ? 'Remove admin access'
                            : 'Give full access to everything'
                        }
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                          full ? 'bg-red-600 text-white' : 'bg-white/90 text-gray-600 hover:bg-white'
                        } ${locked ? 'cursor-default' : 'cursor-pointer'} ${isPending ? 'opacity-50' : ''}`}
                      >
                        {full && <Check size={11} />}
                        {member.isOwner ? 'Owner — full access' : 'Admin — full access'}
                      </button>
                    );
                  })()}

                  {(member.fullAccess ?? member.role === 'admin')
                    ? (
                      <span className={`text-xs ${theme.textMuted} italic self-center`}>
                        holds every access right
                      </span>
                    )
                    : CAPABILITIES.map(cap => {
                      const granted = (member.capabilities || []).includes(cap.id);
                      const key = `${member.id}-${cap.id}`;
                      const isPending = pendingCapability === key;
                      if (!isAdmin && !granted) return null; // non-admin viewers only see what's actually granted
                      return (
                        <button
                          key={cap.id}
                          disabled={!isAdmin || isPending}
                          onClick={() => handleToggleCapability(member, cap.id, granted)}
                          title={isAdmin ? `${granted ? 'Revoke' : 'Grant'} ${cap.label}` : cap.label}
                          // Solid, opaque fills on purpose (not theme.border/textMuted, which have
                          // no background) — academy cards can be any custom design color, so a
                          // borderless/tinted-only chip disappears into it. Matches the same
                          // opaque-pill pattern the role badge above already uses successfully.
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                            granted
                              ? theme.tabActive
                              : 'bg-white/90 text-gray-600 hover:bg-white'
                          } ${isAdmin ? 'cursor-pointer' : 'cursor-default'} ${isPending ? 'opacity-50' : ''}`}
                        >
                          {granted && <Check size={11} />}
                          {cap.label}
                        </button>
                      );
                    })}

                  {!isAdmin && !(member.fullAccess ?? member.role === 'admin')
                    && (member.capabilities || []).length === 0 && (
                    <span className={`text-xs ${theme.textMuted} italic`}>No access granted</span>
                  )}
                </div>
              </div>

              {/* Editing or removing someone who is ALREADY an admin is
                  owner/admin-only server-side (requireAdminAccess in
                  updateStaffRole and removeStaff) — manage_staff covers ordinary
                  staff, not other admins. Match that here so the buttons aren't
                  offered only to fail with a 403. */}
              {canManageStaff && (member.role !== 'admin' || isAdmin) && (!member.isOwner || isOwnerViewer) && (
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => setEditingStaff(member)}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 border ${theme.border} ${theme.text} ${theme.cardHover} rounded-lg text-sm transition-colors`}
                  >
                    <Edit2 size={14} /> Edit
                  </button>
                  <button
                    onClick={() => setDeletingStaff(member)}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 ${theme.buttonDanger} rounded-lg text-sm`}
                  >
                    <Trash2 size={14} /> Remove
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className={`${theme.card} border text-center p-12 rounded-xl`}>
          <UserCheck size={48} className={`mx-auto mb-3 ${theme.textMuted}`} />
          <h5 className={`text-xl font-semibold ${theme.text} mb-2`}>
            {searchQuery ? 'No staff match your search' : 'No staff yet'}
          </h5>
          <p className={theme.textSecondary}>
            {searchQuery
              ? 'Try a different name or clear the search'
              : canManageStaff ? 'Add your first staff member to get started' : 'No staff have been added yet'}
          </p>
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className={`mt-4 px-4 py-2 ${theme.buttonPrimary} rounded-lg text-sm`}>
              Clear Search
            </button>
          )}
          {!searchQuery && canManageStaff && (
            <button onClick={() => setShowInviteModal(true)} className={`mt-4 flex items-center gap-2 px-4 py-2 ${theme.buttonPrimary} rounded-lg mx-auto`}>
              <Plus size={18} /><span>Add First Staff Member</span>
            </button>
          )}
        </div>
      )}

      {/* ── Open Roles Section ── */}
      <div className={`mt-10 pt-8 border-t ${theme.border}`}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className={`text-xl font-bold ${theme.text}`}>Open Roles</h3>
            <p className={`text-sm ${theme.textSecondary} mt-0.5`}>
              {loadingOpenings
                ? 'Loading…'
                : openings.length === 0
                  ? 'No openings posted'
                  : `${openings.length} position${openings.length !== 1 ? 's' : ''} available`}
            </p>
          </div>
          {canManageStaff && (
            <button
              onClick={() => { setEditingOpening(null); setShowOpeningModal(true); }}
              className={`flex items-center gap-1.5 px-3 py-2 border ${theme.border} ${theme.text} ${theme.cardHover} rounded-lg text-sm transition-colors`}
            >
              <Plus size={15} /><span>Post Opening</span>
            </button>
          )}
        </div>

        {loadingOpenings ? (
          <div className={`${theme.card} border text-center p-10 rounded-xl`}>
            <div className={`text-sm ${theme.textSecondary}`}>Loading open positions…</div>
          </div>
        ) : openings.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {openings.map(opening => (
              <OpeningCard
                key={opening.id}
                opening={opening}
                isAdmin={canManageStaff}
                theme={theme}
                onEdit={o => { setEditingOpening(o); setShowOpeningModal(true); }}
                onDelete={setDeletingOpening}
              />
            ))}
          </div>
        ) : (
          <div className={`${theme.card} border text-center p-10 rounded-xl`}>
            <Briefcase size={40} className={`mx-auto mb-3 ${theme.textMuted}`} />
            <p className={`${theme.textSecondary} text-sm`}>
              {canManageStaff
                ? 'Post an opening to attract staff. It will appear in the Jobs section.'
                : 'No open roles at this time.'}
            </p>
            {canManageStaff && (
              <button onClick={() => { setEditingOpening(null); setShowOpeningModal(true); }}
                className={`mt-4 flex items-center gap-2 px-4 py-2 ${theme.buttonPrimary} rounded-lg mx-auto text-sm`}>
                <Plus size={16} /><span>Post First Opening</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Modals ── */}
      <InviteModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        title="Add or Invite Staff"
        staffRoles={assignableRoles}
        checkIfAlreadyExists={checkIfAlreadyExists}
        onAddExistingMember={handleAddExistingMember}
        onInviteByEmail={handleInviteByEmail}
        onInviteByPhone={handleInviteByPhone}
      />

      <RemoveAdminDialog
        member={demotingAdmin}
        assignableRoles={assignableRoles}
        busy={pendingCapability === `${demotingAdmin?.id}-admin`}
        onConfirm={handleRemoveAdmin}
        onCancel={() => setDemotingAdmin(null)}
        theme={theme}
      />

      <EditRoleModal
        isOpen={!!editingStaff}
        staff={editingStaff}
        staffOptions={staff}
        assignableRoles={assignableRoles}
        onClose={() => setEditingStaff(null)}
        onSave={handleEditRole}
        theme={theme}
      />

      <OpeningFormModal
        isOpen={showOpeningModal}
        onClose={() => { setShowOpeningModal(false); setEditingOpening(null); }}
        onSave={handleSaveOpening}
        initialData={editingOpening}
        staffOptions={staff}
        theme={theme}
      />

      <DeleteConfirmDialog
        item={deletingStaff}
        label="Remove Staff Member"
        onConfirm={handleRemoveStaff}
        onCancel={() => setDeletingStaff(null)}
        theme={theme}
      />

      <DeleteConfirmDialog
        item={deletingOpening}
        label="Remove Opening"
        onConfirm={handleDeleteOpening}
        onCancel={() => setDeletingOpening(null)}
        theme={theme}
      />
    </div>
  );
};

export default StaffTab;
